import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, EmptyState, ErrorState, Icon, TableEmptyRow, TableSkeletonRows } from "@sagot/ui";
import { useState } from "react";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { useAgentList } from "../shared/use-engage-data";
import { BookingDecisionDialog } from "./booking-decision-dialog";
import { BookingRow } from "./booking-row";
import { BOOKING_FILTERS, type BookingFilter } from "./bookings-model";
import type { Booking } from "./lead-types";
import { LeadsViewSwitch } from "./leads-view-switch";
import { StageTabs } from "./stage-tabs";
import type { useLeadsRoute } from "./use-leads-route";
import { useBookingList, type Decision } from "./use-bookings";

const PANEL_ID = "leads-panel";
const COLUMNS = 6;

interface BookingsPanelProps {
  route: ReturnType<typeof useLeadsRoute>;
  canEdit: boolean;
}

interface Deciding {
  booking: Booking;
  decision: Decision;
  when: string;
}

function BookingsEmpty({ filter, onShowAll }: { filter: BookingFilter; onShowAll: () => void }) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const agent = useAgentList().data?.[0];
  if (filter !== "all") {
    return (
      <EmptyState
        icon="calendar"
        headingLevel={3}
        title={t("engage.bookings.empty.filtered", { status: t(`engage.bookings.tabs.${filter}`).toLowerCase() })}
        description={t("engage.bookings.empty.filteredBody")}
        action={<Button variant="secondary" onClick={onShowAll}>{t("engage.bookings.empty.showAll")}</Button>}
      />
    );
  }
  return (
    <EmptyState
      icon="calendar"
      headingLevel={3}
      title={t("engage.bookings.empty.title")}
      description={t("engage.bookings.empty.body")}
      action={
        agent ? (
          <Button asChild>
            <AppLink href={consoleRoutes.actions(summary.slug, agent.id)}>
              <Icon name="zap" />
              {t("engage.bookings.empty.cta")}
            </AppLink>
          </Button>
        ) : undefined
      }
    />
  );
}

/** Leads → Bookings: status tabs with the waiting count, rows with Confirm / Decline / Cancel, load more. */
export function BookingsPanel({ route, canEdit }: BookingsPanelProps) {
  const { t } = useTranslation();
  const list = useBookingList(route.bookingFilter);
  const [deciding, setDeciding] = useState<Deciding | null>(null);
  const bookings = (list.data?.pages ?? []).flatMap((page) => page.items);
  const pending = list.data?.pages[0]?.pending ?? 0;
  const tabs = BOOKING_FILTERS.map((value) => ({ value, label: t(`engage.bookings.tabs.${value}`), count: value === "requested" && list.data ? pending : null }));
  const waiting = pending > 0 && route.bookingFilter !== "requested";
  const leadHref = (booking: Booking) => (booking.lead_id ? route.leadHref(booking.lead_id) : null);
  return (
    <section className="card overflow-hidden" aria-label={t("engage.bookings.title")}>
      <div className="flex flex-col gap-3 border-b border-line p-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          <LeadsViewSwitch value={route.view} onValueChange={route.setView} />
          <StageTabs label={t("engage.bookings.tabs.label")} controls={PANEL_ID} value={route.bookingFilter} onChange={(value) => { route.setBookingFilter(value as BookingFilter); }} tabs={tabs} />
        </div>
      </div>
      {waiting ? (
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line bg-warning-soft px-4 py-2 text-sm" role="status">
          <span className="flex items-center gap-2">
            <Icon name="clock" className="size-4 text-warning" />
            {t("engage.bookings.waiting", { count: pending })}
          </span>
          <Button variant="secondary" size="sm" onClick={() => { route.setBookingFilter("requested"); }}>{t("engage.bookings.showRequested")}</Button>
        </div>
      ) : null}
      {list.isError ? (
        <ErrorState title={t("engage.bookings.loadError")} retryLabel={t("common.retry")} onRetry={() => void list.refetch()} />
      ) : (
        <>
          <div className="overflow-x-auto" id={PANEL_ID} role="tabpanel">
            <table className="table">
              <thead>
                <tr>
                  <th>{t("engage.bookings.columns.when")}</th>
                  <th>{t("engage.bookings.columns.service")}</th>
                  <th>{t("engage.bookings.columns.visitor")}</th>
                  <th>{t("engage.bookings.columns.owner")}</th>
                  <th>{t("engage.bookings.columns.status")}</th>
                  <th className="text-right">{t("engage.bookings.columns.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {list.isPending ? (
                  <TableSkeletonRows rows={5} columns={COLUMNS} />
                ) : bookings.length === 0 ? (
                  <TableEmptyRow colSpan={COLUMNS}>
                    <BookingsEmpty filter={route.bookingFilter} onShowAll={() => { route.setBookingFilter("all"); }} />
                  </TableEmptyRow>
                ) : (
                  bookings.map((booking) => (
                    <BookingRow key={booking.id} booking={booking} canEdit={canEdit} leadHref={leadHref(booking)} onDecide={(target, decision, when) => { setDeciding({ booking: target, decision, when }); }} />
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between gap-3 border-t border-line px-4 py-3 text-xs text-fg-subtle">
            <span aria-live="polite">{list.data ? t("engage.bookings.showing", { shown: bookings.length }) : ""}</span>
            {list.hasNextPage ? (
              <Button variant="secondary" size="sm" loading={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
                {t("engage.loadMore")}
              </Button>
            ) : null}
          </div>
        </>
      )}
      {canEdit ? null : <p className="border-t border-line px-4 py-3 text-xs text-fg-subtle">{t("engage.bookings.readOnly")}</p>}
      {deciding ? (
        <BookingDecisionDialog key={`${deciding.booking.id}:${deciding.decision}`} booking={deciding.booking} decision={deciding.decision} when={deciding.when} onClose={() => { setDeciding(null); }} />
      ) : null}
    </section>
  );
}
